<script setup>
/* global axios */
// Renderiza uma ENQUETE do WhatsApp (encaminhada pelo ByCall):
//   "📊 *pergunta*\n1. Opção A\n2. Opção B\n_PID: <id>_"
// - Opções clicáveis → vota (POST poll_vote → ByCall /messages/poll-vote).
// - Agrega os votos de entrada (mensagens "🗳️ Fulano votou: Opção") que chegam
//   DEPOIS desta enquete (até a próxima) e mostra contagem + barras.
// Detecção/roteamento no Message.vue (marcador 📊). O voto de entrada em si é
// exibido de forma compacta pelo PollVote.vue.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useMessageContext } from '../provider.js';
import BaseBubble from 'next/message/bubbles/Base.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const { content, conversationId, id } = useMessageContext();
const route = useRoute();
const selectedChat = useMapGetter('getSelectedChat');

const voting = ref(false);
const votedName = ref('');
const errorMsg = ref('');
const votersDialog = ref(null);

const poll = computed(() => {
  const raw = String(content?.value || '');
  const lines = raw.split('\n');

  let question =
    (raw.match(/📊\s*\*([^*]+)\*/) || [])[1] ||
    (raw.match(/📊\s*(.+)/) || [])[1] ||
    '';
  question = question.replace(/^enquete:\s*/i, '').trim();

  const pollId = (raw.match(/_PID:\s*([^_]+)_/) || [])[1]?.trim() || '';

  const options = lines
    .map(l => (l.match(/^\s*(?:\d+[.)]|[-•▫])\s*(.+)$/) || [])[1])
    .filter(Boolean)
    .map(linha => linha.trim())
    .filter(Boolean);

  return { question, pollId, options };
});

// Agrega os votos de entrada desta enquete lendo as mensagens da conversa.
// Casa por posição: votos entre esta enquete e a próxima (📊), cujo conjunto de
// opções pertence a esta enquete. Último voto de cada pessoa prevalece.
const tally = computed(() => {
  const msgs = selectedChat.value?.messages || [];
  const myIdx = msgs.findIndex(m => m.id === id?.value);
  const optionSet = new Set(poll.value.options);
  const byVoter = new Map(); // voter -> [opções]

  const start = myIdx === -1 ? 0 : myIdx + 1;
  for (let i = start; i < msgs.length; i += 1) {
    const c = String(msgs[i].content || '');
    if (c.includes('📊')) break; // próxima enquete → para

    if (c.includes('🗳️')) {
      const retract = c.match(/🗳️\s*(.+?)\s+retirou o voto/i);
      const m = retract ? null : c.match(/🗳️\s*(.+?)\s+votou:\s*(.+)$/i);
      if (retract) {
        byVoter.delete(retract[1].trim());
      } else if (m) {
        const voter = m[1].trim();
        const opts = m[2]
          .split(',')
          .map(o => o.trim())
          .filter(Boolean);
        // só conta se as opções pertencem a esta enquete (evita casar enquete errada)
        if (opts.length && opts.every(o => optionSet.has(o))) {
          byVoter.set(voter, opts);
        }
      }
    }
  }

  const counts = {};
  const votersByOption = {};
  poll.value.options.forEach(o => {
    counts[o] = 0;
    votersByOption[o] = [];
  });
  byVoter.forEach((opts, voter) =>
    opts.forEach(o => {
      if (counts[o] !== undefined) {
        counts[o] += 1;
        votersByOption[o].push(voter);
      }
    })
  );

  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return { counts, votersByOption, total, hasVotes: total > 0 };
});

const pct = opt => {
  if (!tally.value.total) return 0;
  return Math.round(((tally.value.counts[opt] || 0) / tally.value.total) * 100);
};

const vote = async optName => {
  if (!poll.value.pollId || voting.value) return;
  errorMsg.value = '';
  voting.value = true;
  const prev = votedName.value;
  votedName.value = optName;
  try {
    const base = `/api/v1/accounts/${route.params.accountId}/kanban/connections`;
    await axios.post(`${base}/poll_vote`, {
      conversation_id: conversationId?.value,
      poll_message_id: poll.value.pollId,
      selected_names: [optName],
    });
  } catch (e) {
    votedName.value = prev;
    errorMsg.value = e?.response?.data?.error || 'Falha ao enviar o voto';
  } finally {
    voting.value = false;
  }
};
</script>

<template>
  <BaseBubble
    class="!p-3.5 !max-w-[300px] min-w-[240px]"
    data-bubble-name="poll"
    hide-meta
  >
    <div class="flex flex-col gap-2.5">
      <div class="flex items-center gap-1.5 text-n-slate-11">
        <span class="i-ph-chart-bar-bold size-3.5" />
        <span class="text-[0.6875rem] font-medium uppercase tracking-wide">
          {{ t('CONVERSATION.POLL_BUBBLE.HEADING') }}
        </span>
      </div>

      <div class="font-medium leading-snug text-n-slate-12">
        {{ poll.question }}
      </div>

      <div class="flex flex-col gap-1.5">
        <button
          v-for="(opt, i) in poll.options"
          :key="i"
          type="button"
          :disabled="!poll.pollId || voting"
          class="relative flex items-center w-full gap-2.5 px-3 py-2 overflow-hidden text-left rounded-lg outline outline-1 outline-n-weak bg-n-alpha-1"
          :class="[
            poll.pollId && !voting
              ? 'cursor-pointer hover:outline-n-brand'
              : 'cursor-default',
            votedName === opt ? 'outline-n-brand bg-n-brand/5' : '',
          ]"
          @click="vote(opt)"
        >
          <span
            v-if="tally.hasVotes"
            class="absolute inset-y-0 left-0 bg-n-brand/10"
            :style="{ width: pct(opt) + '%' }"
          />
          <span
            class="relative rounded-full size-4 shrink-0 outline outline-1 flex"
            :class="
              votedName === opt
                ? 'outline-n-brand bg-n-brand'
                : 'outline-n-slate-8'
            "
          >
            <span
              v-if="votedName === opt"
              class="i-ph-check-bold m-auto text-white size-2.5"
            />
          </span>
          <span class="relative flex-1 text-sm text-n-slate-12">{{ opt }}</span>
          <span
            v-if="tally.hasVotes"
            class="relative text-xs tabular-nums text-n-slate-11"
          >
            {{ tally.counts[opt] || 0 }}
          </span>
        </button>
      </div>

      <p v-if="!poll.options.length" class="text-xs italic text-n-slate-10">
        {{ t('CONVERSATION.POLL_BUBBLE.NO_OPTIONS') }}
      </p>

      <div
        v-if="tally.hasVotes"
        class="flex items-center justify-between pt-0.5"
      >
        <span class="text-[0.625rem] text-n-slate-10">
          {{ t('CONVERSATION.POLL_BUBBLE.VOTE_COUNT', { count: tally.total }) }}
        </span>
        <button
          type="button"
          class="text-xs font-medium text-n-brand hover:underline"
          @click="votersDialog?.open()"
        >
          {{ t('CONVERSATION.POLL_BUBBLE.SHOW_VOTES') }}
        </button>
      </div>

      <p v-if="errorMsg" class="text-xs text-n-ruby-11">{{ errorMsg }}</p>
      <p
        v-else-if="!poll.pollId"
        class="text-[0.625rem] italic text-n-slate-10"
      >
        {{ t('CONVERSATION.POLL_BUBBLE.UNAVAILABLE') }}
      </p>
    </div>

    <!-- pop-up "Dados da enquete" (formato WhatsApp): quem votou em cada opção -->
    <Dialog
      ref="votersDialog"
      width="sm"
      :show-cancel-button="false"
      :show-confirm-button="false"
      overflow-y-auto
    >
      <div class="flex flex-col gap-1 -mt-2">
        <div class="flex items-center justify-between">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('CONVERSATION.POLL_BUBBLE.DETAILS') }}
          </h3>
          <button
            type="button"
            class="flex text-n-slate-11 hover:text-n-slate-12"
            @click="votersDialog?.close()"
          >
            <span class="i-ph-x-bold size-4" />
          </button>
        </div>
        <p class="text-sm font-medium text-n-amber-11">{{ poll.question }}</p>
      </div>

      <div class="flex flex-col gap-5 max-h-[60vh]">
        <div
          v-for="(opt, i) in poll.options"
          :key="i"
          class="flex flex-col gap-2.5"
        >
          <div class="flex items-center justify-between">
            <span class="text-sm font-medium text-n-slate-12">{{ opt }}</span>
            <span
              class="text-xs font-medium px-2 py-0.5 rounded-md bg-n-alpha-2 text-n-slate-11 tabular-nums"
            >
              {{
                t('CONVERSATION.POLL_BUBBLE.VOTE_COUNT', {
                  count: tally.counts[opt] || 0,
                })
              }}
            </span>
          </div>
          <div
            v-for="(voter, vi) in tally.votersByOption[opt]"
            :key="vi"
            class="flex items-center gap-2.5"
          >
            <span
              class="i-ph-user-circle-fill size-8 shrink-0 text-n-slate-8"
            />
            <span class="text-sm text-n-slate-12">{{ voter }}</span>
          </div>
          <div
            v-if="!tally.votersByOption[opt]?.length"
            class="text-xs text-n-slate-10"
          >
            {{ t('CONVERSATION.POLL_BUBBLE.NO_VOTES') }}
          </div>
        </div>
      </div>
    </Dialog>
  </BaseBubble>
</template>
